import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DISPLAY_KEY_SEGMENT_LENGTH,
  DISPLAY_KEY_AREA,
  DISPLAY_KEY_MEASUREMENT,
} from '../shapes/ShapeRenderer';

const RulerIcon = ({ size = 14, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}
  >
    <g transform="rotate(-45 12 12)">
      <rect x="2" y="8" width="20" height="8" rx="1" />
      <path d="M6 8v3M10 8v3M14 8v3M18 8v3" />
    </g>
  </svg>
);

const SegmentRulerIcon = ({ size = 14, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}
  >
    <rect x="2" y="11" width="20" height="9" rx="1" />
    <path d="M6 11v3M10 11v3M14 11v3M18 11v3" />
    <path d="M4 5h16" strokeWidth="1.5" />
    <path d="M4 3v4M12 3v4M20 3v4" strokeWidth="1.5" />
    <circle cx="4" cy="5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="20" cy="5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const TotalRulerIcon = ({ size = 14, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}
  >
    <rect x="2" y="11" width="20" height="9" rx="1" />
    <path d="M6 11v3M10 11v3M14 11v3M18 11v3" />
    <path d="M4 5h5M15 5h5" strokeWidth="1.5" />
    <path d="M4 3v4M20 3v4" strokeWidth="1.5" />
    <circle cx="4" cy="5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="20" cy="5" r="1.5" fill="currentColor" stroke="none" />
    <path d="M10 2h4l-2 3 2 3h-4" strokeWidth="1.5" />
  </svg>
);

const AreaIcon = ({ size = 14, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9l6-6M3 15l12-12M3 21l18-18M9 21l12-12M15 21l6-6" strokeWidth="1.5" opacity="0.6" />
  </svg>
);

const OverlayIcon = ({ size = 14, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}
  >
    <path d="M4 20L20 4" strokeWidth="2" />
    <path d="M4 20H20" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
    <path d="M20 20V4" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
    <path d="M12 20 A 8 8 0 0 0 16 16" strokeWidth="1.5" opacity="0.8" />
  </svg>
);

const ChevronLeftIcon = ({ size = 10, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ display: 'block' }}
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = ({ size = 10, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ display: 'block' }}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const DisplayKeysPanel = ({
  displayKeys,
  onDisplayKeysChange,
  measurementSystem,
  displayUnitSystem,
  onDisplayUnitSystemChange,
  allParamKeys = [],
  shapeListVisible,
  overlayEnabled = true,
  onToggleOverlay,
  contextualPanelVisible = false,
}) => {
  const { t } = useTranslation();
  const [isParamDropdownOpen, setIsParamDropdownOpen] = useState(false);
  const paramDropdownRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = e.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key.toLowerCase() === 'e' && onToggleOverlay) {
        e.preventDefault();
        onToggleOverlay();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onToggleOverlay]);

  useEffect(() => {
    if (!isParamDropdownOpen) return;
    const handleClick = (e) => {
      if (paramDropdownRef.current && !paramDropdownRef.current.contains(e.target)) {
        setIsParamDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isParamDropdownOpen]);

  const handleDisplayKeyToggle = (key) => {
    if (onDisplayKeysChange) {
      const newKeys = displayKeys.includes(key)
        ? displayKeys.filter((k) => k !== key)
        : [...displayKeys, key];
      onDisplayKeysChange(newKeys);
    }
  };

  const hasSegmentLength = displayKeys.includes(DISPLAY_KEY_SEGMENT_LENGTH);
  const hasTotalMeasurement = displayKeys.includes(DISPLAY_KEY_MEASUREMENT);

  // Cycle states: Off -> Segments -> Total -> Off
  const handleMeasurementCycle = () => {
    if (onDisplayKeysChange) {
      let newKeys = displayKeys.filter(
        (k) => k !== DISPLAY_KEY_SEGMENT_LENGTH && k !== DISPLAY_KEY_MEASUREMENT
      );

      if (!hasSegmentLength && !hasTotalMeasurement) {
        // Off -> Segments
        newKeys.push(DISPLAY_KEY_SEGMENT_LENGTH);
      } else if (hasSegmentLength) {
        // Segments -> Total
        newKeys.push(DISPLAY_KEY_MEASUREMENT);
      }
      // If hasTotalMeasurement, we go to Off, so newKeys stays filtered.

      onDisplayKeysChange(newKeys);
    }
  };

  // Determine active unit systems
  const isMetricActive =
    displayUnitSystem === 'metric' ||
    (displayUnitSystem === null && measurementSystem === 'metric');
  const isImperialActive =
    displayUnitSystem === 'imperial' ||
    (displayUnitSystem === null && measurementSystem === 'imperial');

  const customParamKeys = allParamKeys.filter(
    (key) =>
      key !== DISPLAY_KEY_SEGMENT_LENGTH &&
      key !== DISPLAY_KEY_AREA &&
      key !== DISPLAY_KEY_MEASUREMENT
  );

  const metricLabel = t('replan.pdfControls.metric') || 'Metric';
  const imperialLabel = t('replan.pdfControls.imperial') || 'Imperial';

  const metricFirst = metricLabel.charAt(0);
  const metricRest = metricLabel.slice(1);

  const imperialFirst = imperialLabel.charAt(0);
  const imperialRest = imperialLabel.slice(1);

  return (
    <div
      className="display-keys-panel"
      style={{
        left: contextualPanelVisible ? 'calc((var(--pico-spacing) * 2.2 + 0.2rem)*2 + 0.2rem)' : 'calc(var(--pico-spacing) * 2.2 + 0.2rem)',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Unit override switchers */}
      <div className="scale-switch unit-selector-switch">
        <button
          className={`switch-btn ${isMetricActive ? 'active' : ''}`}
          onClick={() => onDisplayUnitSystemChange && onDisplayUnitSystemChange('metric')}
          type="button"
          data-tt={`${t('shapeList.metric_desc')}\n${metricLabel}\n${t('common.shortcut', { key: 'M', defaultValue: 'M' })}`}
        >
          <><span className="first-letter">{metricFirst}</span><span className="rest-text">{metricRest}</span></>
        </button>
        <button
          className={`switch-btn ${isImperialActive ? 'active' : ''}`}
          onClick={() => onDisplayUnitSystemChange && onDisplayUnitSystemChange('imperial')}
          type="button"
          data-tt={`${t('shapeList.imperial_desc')}\n${imperialLabel}\n${t('common.shortcut', { key: 'M', defaultValue: 'M' })}`}
        >
          <><span className="first-letter">{imperialFirst}</span><span className="rest-text">{imperialRest}</span></>
        </button>
      </div>

      <div className="vertical-separator"></div>

      {/* Combined Measurement / Segment Length switcher */}
      <button
        className={hasSegmentLength || hasTotalMeasurement ? 'transform-active' : ''}
        onClick={handleMeasurementCycle}
        type="button"
        data-tt={`${t('shapeList.measurement_cycle_desc')}\n${t('shapeList.measurement_cycle') || 'Cycle Measurements'}\n${t('common.shortcut', { key: 'J', defaultValue: 'J' })}`}
        style={{ width: 'auto', padding: '0 8px', maxHeight: '80%', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        {hasSegmentLength ? (
          <SegmentRulerIcon />
        ) : hasTotalMeasurement ? (
          <TotalRulerIcon />
        ) : (
          <RulerIcon />
        )}
        <span>
          {hasSegmentLength
            ? (t('shapeList.segment_length_label') || 'Segments')
            : hasTotalMeasurement
              ? (t('shapeList.total_measurement_label') || 'Total')
              : (t('shapeList.measurement_label') || 'Measure')}
        </span>
      </button>

      {/* Area Button - simple toggle */}
      <button
        className={displayKeys.includes(DISPLAY_KEY_AREA) ? 'transform-active' : ''}
        onClick={() => handleDisplayKeyToggle(DISPLAY_KEY_AREA)}
        type="button"
        data-tt={`${t('shapeList.area_display_desc')}\n${t('shapeList.area_display') || 'Area'}\n${t('common.shortcut', { key: 'A', defaultValue: 'A' })}`}
        style={{ width: 'auto', padding: '0 8px', maxHeight: '80%', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        <AreaIcon />
        <span>{t('shapeList.area_display') || 'Area'}</span>
      </button>

      {/* Overlay Button - simple toggle */}
      <button
        className={overlayEnabled ? 'transform-active' : ''}
        onClick={onToggleOverlay}
        type="button"
        data-tt={`${t('shapeList.overlay_desc')}\nOverlay\n${t('common.shortcut', { key: 'E', defaultValue: 'E' })}`}
        style={{ width: 'auto', padding: '0 8px', maxHeight: '80%', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        <OverlayIcon />
        <span>Overlay</span>
      </button>

      {/* Custom Parameters Dropdown */}
      {customParamKeys.length > 0 && (
        <>
          <div className="vertical-separator"></div>
          <button
            ref={paramDropdownRef}
            className={`param-dropdown-trigger ${displayKeys.some((k) => customParamKeys.includes(k)) ? "transform-active" : ""}`}
            onClick={() => setIsParamDropdownOpen(!isParamDropdownOpen)}
            type="button"
            data-tt={`${t("shapeList.display_params_desc", { defaultValue: "Show custom parameter values on shapes" })}\nParameters\n${t("common.click_open", { defaultValue: "Open" })}`}
            style={{ width: "auto", padding: "0 8px", maxHeight: "80%", display: "inline-flex", alignItems: "center", gap: "4px" }}
          >
            <span>☰</span>
            <span>Params</span>
            {displayKeys.some((k) => customParamKeys.includes(k)) && (
              <span className="param-count">{displayKeys.filter((k) => customParamKeys.includes(k)).length}</span>
            )}
            {isParamDropdownOpen && (
              <div className="param-dropdown-list">
                {customParamKeys.map((key) => (
                  <label key={key} className="param-dropdown-item">
                    <input type="checkbox" checked={displayKeys.includes(key)} onChange={() => handleDisplayKeyToggle(key)} />
                    <span>{key}</span>
                  </label>
                ))}
              </div>
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default DisplayKeysPanel;
